import React, { useState, useEffect } from "react";
import { DeleteSvg, EyeSvg } from "../../utils/svgComponent";
import Header from "../../components/content_wrapper/header";
import { useNavigate } from "react-router-dom";
import { useGetKeyList } from "../../hooks/keyManagement/keyListing";
import Pagination from "../../utils/pagination";
import CustomLaoder from "../../components/customLoader";
import { useDeleteKey } from "../../hooks/keyManagement/deleteKey";
import { toast } from "react-toastify";

interface DeleteModalState {
  open: boolean;
  keyId: string;
  keyName: string;
  keyStatus: number;
}

type SortField = "name" | "createdAt" | "subscriptionExpiry" | "status";
type SortOrder = "asc" | "desc";

const statusMap: { [key: number]: { className: string; label: string; icon: string } } = {
  0: { className: "c-pill--warning", label: "Waiting Dropoff", icon: "fa-clock" },
  1: { className: "c-pill--success", label: "In Keybox",       icon: "fa-box" },
  2: { className: "c-pill--danger",  label: "Not In Keybox",   icon: "fa-box-open" },
  3: { className: "c-pill--danger",  label: "Deleted",         icon: "fa-trash" },
};

const subscriptionPillMap: { [key: string]: string } = {
  Active:    "c-pill--success",
  Expired:   "c-pill--danger",
  Cancelled: "c-pill--warning",
};

// billing_status state machine (docs/PAYMENT_PROCESSING_REVIEW.md §8).
// `active` / missing renders nothing — only trouble states get a pill so the
// column reads as an exceptions list at a glance.
const billingStatusMap: { [key: string]: { className: string; label: string } } = {
  past_due:   { className: "c-pill--warning", label: "Past due" },
  grace:      { className: "c-pill--warning", label: "Grace" },
  restricted: { className: "c-pill--danger",  label: "Restricted" },
  canceled:   { className: "c-pill--muted",   label: "Canceled" },
};

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const SortIcon: React.FC<{ field: SortField; active: SortField; order: SortOrder }> = ({ field, active, order }) => {
  if (field !== active) return <i className="fa-solid fa-sort ms-1 text-muted" style={{ opacity: 0.4, fontSize: "0.7rem" }} />;
  return <i className={`fa-solid fa-sort-${order === "asc" ? "up" : "down"} ms-1`} style={{ fontSize: "0.7rem", color: "#72bcb8" }} />;
};

const KeyManagementListing: React.FC = () => {
  const [pageNumber, setPageNumber] = useState<any>(1);
  const [countData, setCountData] = useState<any>(20);
  const [searchInput, setSearchInput] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("active");
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>("");
  const [billingFilter, setBillingFilter] = useState<string>("");
  const [sortBy, setSortBy] = useState<SortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  const [deleteModal, setDeleteModal] = useState<DeleteModalState>({
    open: false, keyId: "", keyName: "", keyStatus: 0,
  });
  const [deleteReason, setDeleteReason] = useState("");
  const [keyHostNotified, setKeyHostNotified] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPageNumber(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: GetKey, isLoading } = useGetKeyList({
    page: pageNumber,
    limit: countData,
    search: debouncedSearch,
    status: statusFilter || undefined,
    subscriptionStatus: subscriptionFilter || undefined,
    billingStatus: billingFilter || undefined,
    sortBy,
    sortOrder,
  });

  const { mutate: deleteKey } = useDeleteKey();
  const navigate = useNavigate();

  const handleClick = (id: string, status: any) => {
    navigate("/keymanagementdetail", { state: { id, status } });
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setPageNumber(1);
  };

  const resetFilters = () => {
    setSearchInput("");
    setStatusFilter("");
    setSubscriptionFilter("");
    setBillingFilter("");
    setSortBy("createdAt");
    setSortOrder("desc");
    setPageNumber(1);
  };

  const hasActiveFilters = searchInput || statusFilter || subscriptionFilter || billingFilter;

  const openDeleteModal = (e: React.MouseEvent, id: string, name: string, status: number) => {
    e.stopPropagation();
    setDeleteReason("");
    setKeyHostNotified(false);
    setDeleteModal({ open: true, keyId: id, keyName: name, keyStatus: status });
  };

  const closeDeleteModal = () => {
    if (deleteSubmitting) return;
    setDeleteModal({ open: false, keyId: "", keyName: "", keyStatus: 0 });
    setDeleteReason("");
    setKeyHostNotified(false);
  };

  const handleConfirmDelete = () => {
    if (!deleteReason.trim()) {
      toast.error("Please provide a reason for deletion");
      return;
    }
    if (deleteModal.keyStatus === 1 && !keyHostNotified) {
      toast.error("Please confirm the key host has been notified before proceeding");
      return;
    }
    setDeleteSubmitting(true);
    deleteKey(
      {
        key_id: deleteModal.keyId,
        reason: deleteReason.trim(),
        ...(deleteModal.keyStatus === 1 && { key_host_notified: true }),
      },
      {
        onSuccess: () => {
          toast.success("Key deleted successfully");
          setDeleteSubmitting(false);
          closeDeleteModal();
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.message || "Failed to delete key");
          setDeleteSubmitting(false);
        },
      }
    );
  };

  const handlePrevPage = () => setPageNumber((p: any) => p - 1);
  const handleNextPage = () => setPageNumber((p: any) => p + 1);
  const handleRowLimitChange = (newValue: number) => setCountData(newValue);

  const keyList = GetKey?.data?.keyList ?? [];
  const totalRecords = GetKey?.data?.totalRecords ?? 0;
  const totalPages = GetKey?.data?.totalPages ?? 1;

  const thStyle: React.CSSProperties = { cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" };

  return (
    <div className="main-wrapper">
      <Header title="Keys" />
      <CustomLaoder loader={isLoading}>
        <div className="content-wrapper">
          <section className="main-section coach-section spacer">
            <div className="container">
              <div className="wrapper-card">

                {/* ── Filters row ── */}
                <div className="row gy-2 mb-3 align-items-end">
                  {/* Search */}
                  <div className="col-12 col-md-3">
                    <div className="form-block p-relative" style={{ position: "relative" }}>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by key name or owner email…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        autoComplete="off"
                      />
                      <span className="search-icon" />
                      {searchInput && (
                        <button
                          type="button"
                          onClick={() => setSearchInput("")}
                          style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", padding: 0, cursor: "pointer", color: "#aaa" }}
                          title="Clear"
                        >
                          <i className="fa-solid fa-circle-xmark" />
                        </button>
                      )}
                      {isLoading && (
                        <span className="spinner-border spinner-border-sm text-secondary" style={{ position: "absolute", right: searchInput ? "34px" : "10px", top: "50%", transform: "translateY(-50%)" }} />
                      )}
                    </div>
                  </div>

                  {/* Key status filter */}
                  <div className="col-6 col-md-2">
                    <label className="form-label small text-muted mb-1">Key Status</label>
                    <select
                      className="form-select"
                      value={statusFilter}
                      onChange={(e) => { setStatusFilter(e.target.value); setPageNumber(1); }}
                    >
                      <option value="">All statuses</option>
                      <option value="active">Active (any)</option>
                      <option value="1">In Keybox</option>
                      <option value="0">Waiting Dropoff</option>
                      <option value="2">Not In Keybox</option>
                      <option value="deleted">Deleted</option>
                    </select>
                  </div>

                  {/* Subscription status filter */}
                  <div className="col-6 col-md-2">
                    <label className="form-label small text-muted mb-1">Subscription</label>
                    <select
                      className="form-select"
                      value={subscriptionFilter}
                      onChange={(e) => { setSubscriptionFilter(e.target.value); setPageNumber(1); }}
                    >
                      <option value="">All subscriptions</option>
                      <option value="Active">Active</option>
                      <option value="Expired">Expired</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>

                  {/* Payment (billing_status) filter */}
                  <div className="col-6 col-md-2">
                    <label className="form-label small text-muted mb-1">Payment</label>
                    <select
                      className="form-select"
                      value={billingFilter}
                      onChange={(e) => { setBillingFilter(e.target.value); setPageNumber(1); }}
                    >
                      <option value="">All payment states</option>
                      <option value="attention">Needs attention</option>
                      <option value="past_due">Past due</option>
                      <option value="grace">Grace period</option>
                      <option value="restricted">Restricted</option>
                      <option value="canceled">Canceled</option>
                      <option value="active">Active</option>
                    </select>
                  </div>

                  {/* Result count + reset */}
                  <div className="col-12 col-md-3 d-flex align-items-center justify-content-between gap-2">
                    {!isLoading && totalRecords > 0 && (
                      <span className="text-muted small">
                        {totalRecords.toLocaleString()} key{totalRecords !== 1 ? "s" : ""}
                        {debouncedSearch && <span> matching <em>"{debouncedSearch}"</em></span>}
                      </span>
                    )}
                    {hasActiveFilters && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary ms-auto"
                        onClick={resetFilters}
                        title="Clear all filters"
                      >
                        <i className="fa-solid fa-filter-circle-xmark me-1" />
                        Clear filters
                      </button>
                    )}
                  </div>
                </div>

                {/* ── Table ── */}
                <div className="col-xl-12">
                  <div className="table-blk table-responsive">
                    <table id="myTable" className="display border-0">
                      <thead>
                        <tr>
                          <th style={thStyle} onClick={() => handleSort("name")}>
                            Key <SortIcon field="name" active={sortBy} order={sortOrder} />
                          </th>
                          <th>Owner</th>
                          <th>Host / Location</th>
                          <th style={thStyle} onClick={() => handleSort("subscriptionExpiry")}>
                            Subscription <SortIcon field="subscriptionExpiry" active={sortBy} order={sortOrder} />
                          </th>
                          <th>Payment</th>
                          <th style={thStyle} onClick={() => handleSort("createdAt")}>
                            Added <SortIcon field="createdAt" active={sortBy} order={sortOrder} />
                          </th>
                          <th style={thStyle} onClick={() => handleSort("status")}>
                            Status <SortIcon field="status" active={sortBy} order={sortOrder} />
                          </th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {keyList.map((items: any, index: number) => {
                          const subClass = subscriptionPillMap[items.keySubscriptionStatus] ?? "c-pill--warning";
                          const stPill = statusMap[items.status] ?? { className: "c-pill--danger", label: "Unknown", icon: "fa-question" };
                          const hasLocation = items.storeName || items.boxId;
                          const billingPill = billingStatusMap[items.billingStatus];
                          const paymentErrMsg = items.lastPaymentError?.message;

                          return (
                            <tr
                              key={index}
                              onClick={() => handleClick(items._id, items.status)}
                              style={{ cursor: "pointer", opacity: items.status === 3 ? 0.6 : 1 }}
                            >
                              <td>
                                <span className="fw-semibold">{items.name}</span>
                              </td>

                              <td>
                                {items.keyOwnerName && (
                                  <div style={{ fontSize: "0.875rem", fontWeight: 600 }}>{items.keyOwnerName}</div>
                                )}
                                <div className="text-muted" style={{ fontSize: "0.8rem" }}>{items.keyOwnerEmail || "—"}</div>
                              </td>

                              <td>
                                {hasLocation ? (
                                  <>
                                    {items.storeName && <div style={{ fontSize: "0.875rem" }}>{items.storeName}</div>}
                                    {items.boxId && (
                                      <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                                        Box {items.boxId}
                                        {items.keySlotNumber != null && <span> · Slot {items.keySlotNumber}</span>}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>

                              <td>
                                {items.keySubscriptionStatus ? (
                                  <>
                                    <span className={`c-pill ${subClass}`} style={{ fontSize: "0.75rem" }}>
                                      {items.keySubscriptionStatus}
                                    </span>
                                    {items.subscriptionExpiresAt && (
                                      <div className="text-muted" style={{ fontSize: "0.75rem", marginTop: "2px" }}>
                                        {items.keySubscriptionStatus === "Active" ? "Expires" : "Expired"}{" "}
                                        {formatDate(items.subscriptionExpiresAt)}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>

                              <td>
                                {billingPill ? (
                                  <>
                                    <span
                                      className={`c-pill ${billingPill.className}`}
                                      style={{ fontSize: "0.75rem" }}
                                    >
                                      {billingPill.label}
                                    </span>
                                    {paymentErrMsg && (
                                      <div
                                        className="text-muted"
                                        style={{ fontSize: "0.72rem", marginTop: "2px", maxWidth: "180px" }}
                                        title={paymentErrMsg}
                                      >
                                        {paymentErrMsg}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>

                              <td>
                                <span style={{ fontSize: "0.85rem" }}>{formatDate(items.createdAt)}</span>
                              </td>

                              <td>
                                <span className={`c-pill ${stPill.className}`} style={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                                  <i className={`fa-solid ${stPill.icon} me-1`} style={{ fontSize: "0.65rem" }} />
                                  {stPill.label}
                                </span>
                              </td>

                              <td onClick={(e) => e.stopPropagation()}>
                                <span className="table-action">
                                  <a
                                    href="#"
                                    onClick={(e) => { e.preventDefault(); handleClick(items._id, items.status); }}
                                    className="table-eye"
                                    title="View details"
                                  >
                                    <EyeSvg />
                                  </a>
                                  {items.status !== 3 && (
                                    <a
                                      href="#"
                                      onClick={(e) => { e.preventDefault(); openDeleteModal(e, items._id, items.name, items.status); }}
                                      className="table-eye"
                                      title="Delete key"
                                    >
                                      <DeleteSvg color="#72BCB8" />
                                    </a>
                                  )}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {!isLoading && keyList.length === 0 && (
                      <div className="d-flex flex-column justify-content-center align-items-center my-5 sub-heading" style={{ gap: "8px" }}>
                        <i className="fa-solid fa-key text-muted" style={{ fontSize: "2rem", opacity: 0.35 }} />
                        <h2 className="m-0 text-muted">
                          {debouncedSearch ? `No keys matching "${debouncedSearch}"` : "No keys found"}
                        </h2>
                        {hasActiveFilters && (
                          <button type="button" className="btn btn-sm btn-outline-secondary mt-2" onClick={resetFilters}>
                            Clear filters
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <Pagination
                  totalPages={totalPages}
                  currentPage={pageNumber}
                  handlePrevPage={handlePrevPage}
                  handleNextPage={handleNextPage}
                  handleRowLimitChange={handleRowLimitChange}
                />
              </div>
            </div>
          </section>
        </div>
      </CustomLaoder>

      {/* ── Delete Key Modal ── */}
      {deleteModal.open && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-bottom-0 pb-0">
                <h5 className="modal-title text-danger">
                  <i className="fa-solid fa-triangle-exclamation me-2" />
                  Delete Key
                </h5>
                <button type="button" className="btn-close" onClick={closeDeleteModal} disabled={deleteSubmitting} />
              </div>
              <div className="modal-body">
                <p className="mb-1">
                  You are about to permanently delete <strong>{deleteModal.keyName}</strong>.
                </p>
                <div className="alert alert-warning py-2 small mb-3">
                  <i className="fa-solid fa-bolt me-1" />
                  This will <strong>immediately cancel</strong> the owner's Stripe subscription and cannot be undone.
                </div>
                {deleteModal.keyStatus === 1 && (
                  <div className="alert alert-danger py-2 small mb-3">
                    <i className="fa-solid fa-box me-1" />
                    This key is currently <strong>in a keybox</strong>. The physical key will remain in the box — the key host must be asked to retrieve it.
                  </div>
                )}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Reason for deletion <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Enter the reason for deleting this key…"
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    disabled={deleteSubmitting}
                  />
                </div>
                {deleteModal.keyStatus === 1 && (
                  <div className="form-check mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="keyHostNotified"
                      checked={keyHostNotified}
                      onChange={(e) => setKeyHostNotified(e.target.checked)}
                      disabled={deleteSubmitting}
                    />
                    <label className="form-check-label small" htmlFor="keyHostNotified">
                      I have contacted the key host to retrieve the physical key <span className="text-danger">*</span>
                    </label>
                  </div>
                )}
              </div>
              <div className="modal-footer border-top-0 pt-0">
                <button className="btn btn-secondary" onClick={closeDeleteModal} disabled={deleteSubmitting}>
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={deleteSubmitting || !deleteReason.trim() || (deleteModal.keyStatus === 1 && !keyHostNotified)}
                >
                  {deleteSubmitting
                    ? <><span className="spinner-border spinner-border-sm me-2" />Deleting…</>
                    : "Delete Key"
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KeyManagementListing;
